"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { BrainView } from "./brainMobileViews";
const GLOBAL_VIEWS = new Set([BrainView.DEFAULT, BrainView.WAVES, BrainView.PROFILE_FEED, BrainView.MESSAGES, BrainView.NOTIFICATIONS]);
const NON_WAVE_VIEWS = new Set([BrainView.NOTIFICATIONS, BrainView.MESSAGES, BrainView.WAVES, BrainView.PROFILE_FEED]);
interface UseBrainMobileActiveViewParams {
  readonly firstDecisionDone: boolean;
  readonly isApp: boolean;
  readonly isCompleted: boolean;
  readonly hasAuthenticatedProfile: boolean;
  readonly hasPolls?: boolean | undefined;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
  readonly isApproveWave?: boolean | undefined;
  readonly pathname: string;
  readonly searchParams: ReadonlyURLSearchParams;
  readonly wave: ApiWave | null | undefined;
  readonly waveId: string | null;
}
interface UseBrainMobileActiveViewResult {
  readonly activeView: BrainView;
  readonly onViewChange: (view: BrainView) => void;
}
interface WaveViewState {
  readonly firstDecisionDone: boolean;
  readonly hasAuthenticatedProfile: boolean;
  readonly hasPolls: boolean;
  readonly isApproveWave: boolean;
  readonly isCompleted: boolean;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
}
function getRouteDefaultView({
  createParam,
  isApp,
  pathname,
  viewParam,
  waveId
}: Pick<UseBrainMobileActiveViewParams, "isApp" | "pathname" | "waveId"> & {
  readonly createParam: string | null;
  readonly viewParam: string | null;
}): BrainView | null {
  if (createParam && isApp) {
    return BrainView.DEFAULT;
  }
  if (!waveId && pathname === "/notifications" || !waveId && viewParam === "notifications") {
    return BrainView.NOTIFICATIONS;
  }
  if (!waveId && pathname === "/messages" || !waveId && viewParam === "messages") {
    return BrainView.MESSAGES;
  }
  if (!waveId && isApp && pathname === "/waves" && viewParam === "profile-feed") {
    return BrainView.PROFILE_FEED;
  }
  if (!waveId && pathname === "/waves" || !waveId && viewParam === "waves") {
    return BrainView.WAVES;
  }
  if (pathname === "/" && !waveId && !viewParam) {
    return BrainView.DEFAULT;
  }
  return null;
}
function getWaveDefaultView({
  hasLoadedWave,
  isApproveWave,
  isCompleted,
  isRankWave
}: Pick<WaveViewState, "isApproveWave" | "isCompleted" | "isRankWave"> & {
  readonly hasLoadedWave: boolean;
}): BrainView {
  return hasLoadedWave && isRankWave && !isApproveWave && isCompleted ? BrainView.SUBMISSIONS : BrainView.DEFAULT;
}
function getWaveViewAvailability({
  firstDecisionDone,
  hasAuthenticatedProfile,
  hasPolls = false,
  isApproveWave,
  isCompleted,
  isCurationWave,
  isMemesWave,
  isRankWave
}: WaveViewState): Partial<Record<BrainView, boolean>> {
  const isCompetitionWave = isRankWave || isApproveWave;
  return {
    [BrainView.LEADERBOARD]: isCompetitionWave,
    [BrainView.SUBMISSIONS]: isRankWave && !isApproveWave && isCompleted,
    [BrainView.SALES]: isCurationWave,
    [BrainView.WINNERS]: isCompetitionWave && (isApproveWave || firstDecisionDone),
    [BrainView.OUTCOME]: isCompetitionWave && !isCurationWave,
    [BrainView.MY_VOTES]: isCompetitionWave && (isCurationWave || hasAuthenticatedProfile),
    [BrainView.POLLS]: hasPolls,
    [BrainView.FAQ]: isMemesWave
  };
}
function normalizeActiveView({
  activeView,
  firstDecisionDone,
  hasWave,
  isCompleted,
  hasAuthenticatedProfile,
  hasPolls,
  isCurationWave,
  isMemesWave,
  isRankWave,
  isApproveWave = false,
  routeDefaultView,
  wave
}: {
  readonly activeView: BrainView;
  readonly firstDecisionDone: boolean;
  readonly hasWave: boolean;
  readonly isCompleted: boolean;
  readonly hasAuthenticatedProfile: boolean;
  readonly hasPolls: boolean;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
  readonly isApproveWave?: boolean | undefined;
  readonly routeDefaultView: BrainView | null;
  readonly wave: ApiWave | null | undefined;
}): BrainView {
  const hasLoadedWave = wave !== null && wave !== undefined;
  const waveViewState: WaveViewState = {
    firstDecisionDone,
    hasAuthenticatedProfile,
    hasPolls,
    isApproveWave,
    isCompleted,
    isCurationWave,
    isMemesWave,
    isRankWave
  };
  const waveDefaultView = getWaveDefaultView({
    hasLoadedWave,
    isApproveWave,
    isCompleted,
    isRankWave
  });
  if (!hasWave) {
    if (!GLOBAL_VIEWS.has(activeView)) {
      return routeDefaultView ?? BrainView.DEFAULT;
    }
    return activeView;
  }
  if (!wave) {
    return activeView;
  }
  if (NON_WAVE_VIEWS.has(activeView)) {
    return BrainView.DEFAULT;
  }
  if (activeView === BrainView.LEADERBOARD && waveDefaultView === BrainView.SUBMISSIONS) {
    return BrainView.SUBMISSIONS;
  }
  const isCurrentViewAvailable = getWaveViewAvailability(waveViewState)[activeView] ?? true;
  return isCurrentViewAvailable ? activeView : waveDefaultView;
}
interface ActiveViewSelection {
  readonly contextToken: symbol;
  readonly view: BrainView;
}
export function useBrainMobileActiveView(t0) {
  const $ = _c(29);
  const {
    firstDecisionDone,
    isApp,
    isCompleted,
    hasAuthenticatedProfile,
    hasPolls: t1,
    isCurationWave,
    isMemesWave,
    isRankWave,
    isApproveWave: t2,
    pathname,
    searchParams,
    wave,
    waveId
  } = t0;
  const hasPolls = t1 === undefined ? false : t1;
  const isApproveWave = t2 === undefined ? false : t2;
  const [selection, setSelection] = useState(null);
  const hasWave = Boolean(waveId);
  let onViewChange;
  let t3;
  if ($[0] !== firstDecisionDone || $[1] !== hasAuthenticatedProfile || $[2] !== hasPolls || $[3] !== hasWave || $[4] !== isApp || $[5] !== isApproveWave || $[6] !== isCompleted || $[7] !== isCurationWave || $[8] !== isMemesWave || $[9] !== isRankWave || $[10] !== pathname || $[11] !== searchParams || $[12] !== selection || $[13] !== wave || $[14] !== waveId) {
    const viewParam = searchParams.get("view");
    const createParam = searchParams.get("create");
    const routeDefaultView = getRouteDefaultView({
      createParam,
      isApp,
      pathname,
      viewParam,
      waveId
    });
    const shellContextKey = `shell:${pathname}:${viewParam ?? ""}`;
    const currentContextKey = waveId ? `wave:${waveId}` : shellContextKey;
    let t4;
    if ($[17] !== currentContextKey) {
      t4 = Symbol(currentContextKey);
      $[17] = currentContextKey;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    const currentContextToken = t4;
    const hasLoadedWave = wave !== null && wave !== undefined;
    const t5 = hasWave && hasLoadedWave;
    let t6;
    if ($[19] !== isApproveWave || $[20] !== isCompleted || $[21] !== isRankWave || $[22] !== t5) {
      t6 = getWaveDefaultView({
        hasLoadedWave: t5,
        isApproveWave,
        isCompleted,
        isRankWave
      });
      $[19] = isApproveWave;
      $[20] = isCompleted;
      $[21] = isRankWave;
      $[22] = t5;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    const waveDefaultView = t6;
    const baseView = hasWave ? waveDefaultView : routeDefaultView ?? BrainView.DEFAULT;
    const candidateView = selection?.contextToken === currentContextToken ? selection.view : baseView;
    let t7;
    if ($[24] !== currentContextToken) {
      t7 = view => {
        setSelection({
          contextToken: currentContextToken,
          view
        });
      };
      $[24] = currentContextToken;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    onViewChange = t7;
    t3 = normalizeActiveView({
      activeView: candidateView,
      firstDecisionDone,
      hasWave,
      isCompleted,
      hasAuthenticatedProfile,
      hasPolls,
      isCurationWave,
      isMemesWave,
      isRankWave,
      isApproveWave,
      routeDefaultView,
      wave
    });
    $[0] = firstDecisionDone;
    $[1] = hasAuthenticatedProfile;
    $[2] = hasPolls;
    $[3] = hasWave;
    $[4] = isApp;
    $[5] = isApproveWave;
    $[6] = isCompleted;
    $[7] = isCurationWave;
    $[8] = isMemesWave;
    $[9] = isRankWave;
    $[10] = pathname;
    $[11] = searchParams;
    $[12] = selection;
    $[13] = wave;
    $[14] = waveId;
    $[15] = onViewChange;
    $[16] = t3;
  } else {
    onViewChange = $[15];
    t3 = $[16];
  }
  const activeView = t3;
  let t4;
  if ($[26] !== activeView || $[27] !== onViewChange) {
    t4 = {
      activeView,
      onViewChange
    };
    $[26] = activeView;
    $[27] = onViewChange;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  return t4;
}